/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { TokenMetadata, TokenConfig } from '../types/tokens';
import { RankedToken, RankTier, RankingMetrics } from '../types/ranking';
import { calculateTokenScore } from './utils/rating';

// Weights for the composite score calculation
const WEIGHTS = {
  MCAP: 0.35,
  VOLUME: 0.25,
  LIQUIDITY: 0.2,
  TRUST: 0.2
};

/**
 * Calculates a composite score (0-1000) based on weighted metrics.
 */
export const calculateCompositeScore = (metrics: RankingMetrics): number => {
    // Normalize values against "Moonshot" targets
    // Target Mcap: $50k for high rank
    // Target Vol: $10k
    // Target Liq: $5k
    
    const normMcap = Math.min(metrics.marketCap / 50_000, 1) * 100;
    const normVol = Math.min(metrics.volume24h / 10_000, 1) * 100;
    const normLiq = Math.min(metrics.liquidity / 5_000, 1) * 100;
    const normTrust = metrics.trustScore; // Already 0-100

    const rawScore = 
        (normMcap * WEIGHTS.MCAP) +
        (normVol * WEIGHTS.VOLUME) +
        (normLiq * WEIGHTS.LIQUIDITY) +
        (normTrust * WEIGHTS.TRUST);
    
    return Math.floor(rawScore * 10); // Scale to 0-1000
};

export const determineTier = (score: number): RankTier => {
    if (score >= 850) return RankTier.DIAMOND;
    if (score >= 600) return RankTier.GOLD;
    if (score >= 300) return RankTier.SILVER;
    if (score >= 100) return RankTier.BRONZE;
    return RankTier.UNRANKED;
};

/**
 * Maps TokenMetadata to a mocked TokenConfig structure for scoring compatibility
 */
const mapMetadataToConfig = (meta: TokenMetadata): Partial<TokenConfig> => {
    return {
        name: meta.name,
        description: meta.description,
        imageUrl: meta.image,
        headerImageUrl: meta.header_image,
        website: meta.properties?.website,
        twitter: meta.properties?.twitter,
        telegram: meta.properties?.telegram,
        chain: meta.properties?.chain,
        tokenStandard: meta.properties?.tokenStandard,
        extensions: meta.properties?.extensions
    };
};

/**
 * Main function to process a list of tokens and return them ranked.
 */
export const generateRankings = (tokens: TokenMetadata[]): RankedToken[] => {
    const ranked: RankedToken[] = tokens.map(token => {
        // 1. Calculate Static Trust Score
        const trustScore = calculateTokenScore(mapMetadataToConfig(token));

        // 2. Generate/Simulate Dynamic Metrics 
        // (In a real app, these come from an indexer or DB)
        const seed = (token.name.length + token.symbol.length);
        const randomFactor = Math.random();
        
        // Deterministic simulation based on simple hash of properties to keep UI stable-ish
        const mcapBase = 2000;
        const simulatedMcap = mcapBase + (seed * 500) + (randomFactor * 1000);
        
        const mockMetrics: RankingMetrics = {
            marketCap: simulatedMcap,
            volume24h: simulatedMcap * (0.1 + Math.random() * 0.2), // 10-30% of mcap
            liquidity: simulatedMcap * 0.2, // 20% liquidity
            holders: Math.floor(seed * 2 + Math.random() * 50),
            trustScore,
            ageHours: Math.random() * 72
        };

        const score = calculateCompositeScore(mockMetrics);

        return {
            token,
            rank: 0, // Assigned after sort
            score,
            tier: determineTier(score),
            metrics: mockMetrics,
            trend: Math.random() > 0.6 ? 'UP' : (Math.random() > 0.5 ? 'DOWN' : 'STABLE')
        };
    });

    // Sort by Score Descending
    ranked.sort((a, b) => b.score - a.score);

    // Assign Rank indices
    return ranked.map((item, index) => ({
        ...item,
        rank: index + 1
    }));
};